import { useCallback, useState } from 'react';
import { defineMessages } from 'react-intl';
import { useMixpanel } from '../../contexts/MixpanelContext';
import { useIntl } from '../../hooks';
import { MixpanelEvent } from '../../tokens';
import { Button, ButtonVariants } from '../Button';
import { Box, Flex, VStack } from '../Core';
import { Dialog } from '../Dialog';
import type { DialogProps } from '../Dialog/types';
import { Checkbox } from '../Form/Checkbox';
import { FormControlSizes } from '../Form/types';
import { IconName } from '../Icons';
import { FormattedMessage } from '../Intl';
import type { ExportGridMode } from './utils';

const messages = defineMessages({
  export: {
    defaultMessage: 'Export',
    id: 'BlotterTable.export',
  },
  exportAllData: {
    defaultMessage: 'Export All Data (CSV)',
    id: 'BlotterTable.exportAllData',
  },
  exportFilteredDataCSV: {
    defaultMessage: 'Export Filtered Data (CSV)',
    id: 'BlotterTable.exportFilteredDataCSV',
  },
  exportFilteredDataExcel: {
    defaultMessage: 'Export Filtered Data (Excel)',
    id: 'BlotterTable.exportFilteredDataExcel',
  },
  cancel: {
    defaultMessage: 'Cancel',
    id: 'BlotterTable.cancel',
  },
});

export interface ExportButtonProps {
  onExportAction: () => void;
}

export const ExportButton = (props: ExportButtonProps) => {
  return (
    <Flex w="100%" h="100%" justifyContent="center" alignItems="center">
      <Button
        startIcon={IconName.DocumentDownload}
        variant={ButtonVariants.Default}
        size={FormControlSizes.Small}
        onClick={() => props.onExportAction()}
      >
        <FormattedMessage {...messages.export} />
      </Button>
    </Flex>
  );
};

export interface ExportDialogProps extends DialogProps {
  handleExportServerData: () => void;
  handleExportLocalData: (mode: ExportGridMode) => void;
}

export const ExportDialog = ({ handleExportLocalData, handleExportServerData, ...props }: ExportDialogProps) => {
  const mixpanel = useMixpanel();
  const [exportType, setExportType] = useState<'filtered-csv' | 'filtered-excel' | 'all'>('filtered-csv');
  const { formatMessage } = useIntl();

  const handleConfirm = useCallback(() => {
    mixpanel.track(exportType === 'filtered-excel' ? MixpanelEvent.ExportRowsToExcel : MixpanelEvent.ExportRows, {
      type: exportType,
    });
    if (exportType === 'filtered-csv') {
      handleExportLocalData('CSV');
    }

    if (exportType === 'filtered-excel') {
      handleExportLocalData('Excel');
    }

    if (exportType === 'all') {
      handleExportServerData();
    }
  }, [exportType, handleExportLocalData, handleExportServerData, mixpanel]);

  return (
    <Dialog
      {...props}
      onConfirm={handleConfirm}
      confirmLabel={formatMessage(messages.export)}
      cancelLabel={formatMessage(messages.cancel)}
      title={formatMessage(messages.export)}
      stretchButtons
    >
      <Box>
        <VStack w="200px" h="75px" justifyContent="space-between" alignItems="flex-start">
          <Checkbox checked={exportType === 'filtered-csv'} onChange={() => setExportType('filtered-csv')}>
            <FormattedMessage {...messages.exportFilteredDataCSV} />
          </Checkbox>
          <Checkbox checked={exportType === 'filtered-excel'} onChange={() => setExportType('filtered-excel')}>
            <FormattedMessage {...messages.exportFilteredDataExcel} />
          </Checkbox>
          <Checkbox checked={exportType === 'all'} onChange={() => setExportType('all')}>
            <FormattedMessage {...messages.exportAllData} />
          </Checkbox>
        </VStack>
      </Box>
    </Dialog>
  );
};
